/**
* PageHero — brand-standard hero used across service and landing pages.
*
* Deep navy background with gold accents, matching the official Thai Notary
* Law seal. Optional eyebrow, primary/secondary CTAs, and trust markers.
*/
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

export interface PageHeroCta {
  label: string;
  to?: string;
  href?: string;
}

export interface PageHeroProps {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  primaryCta?: PageHeroCta;
  secondaryCta?: PageHeroCta;
  /** Small trust/credential badges shown under the CTAs. */
  badges?: string[];
  /** Optional right-column visual (image, illustration, card). */
  children?: ReactNode;
  align?: "left" | "center";
}

function CtaButton({
  cta,
  variant,
}: {
  cta: PageHeroCta;
  variant: "primary" | "secondary";
}) {
  const base =
    "inline-flex min-h-[48px] w-full items-center justify-center rounded-md px-6 py-3 text-[15px] font-semibold tracking-wide transition-all duration-200 sm:w-auto sm:text-sm";

  const styles =
    variant === "primary"
      ? "bg-[var(--gold)] text-[var(--primary)] shadow-[var(--shadow-gold)] hover:brightness-110"
      : "border border-[var(--gold)]/60 text-[var(--gold)] hover:bg-[var(--gold)]/10";
  const className = `${base} ${styles}`;
  if (cta.to) {
    return (
      <Link to={cta.to} className={className}>
        {cta.label}
      </Link>
    );
  }
  return (
    <a href={cta.href ?? "#"} className={className}>
      {cta.label}
    </a>
  );
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  badges,
  children,
  align = "left",
}: PageHeroProps) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "var(--gradient-hero)" }}
    >
      {/* Decorative gold ornament */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ background: "var(--gradient-gold)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--gold) 50%, transparent)" }}
      />
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-6 sm:py-20 md:gap-12 md:py-28 lg:grid-cols-12">
        <div className={`lg:col-span-7 ${alignCls} max-w-3xl`}>
          {eyebrow ? (
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-[var(--gold)] sm:text-xs sm:tracking-[0.2em]">
              <span className="h-1 w-1 rounded-full bg-[var(--gold)]" />
              {eyebrow}
            </p>
          ) : null}
          <h1 data-speakable className="text-[28px] font-bold leading-[1.25] tracking-tight text-[var(--primary-foreground)] sm:text-4xl md:text-5xl lg:text-6xl">
            {title}
          </h1>
          {subtitle ? (
            <p data-speakable className="answer-summary speakable-summary lead mt-5 text-base leading-[1.75] text-[var(--primary-foreground)]/80 sm:mt-6 sm:text-lg md:text-xl">
              {subtitle}
            </p>
          ) : null}

          {(primaryCta || secondaryCta) && (
            <div
              className={`mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap ${align === "center" ? "sm:justify-center" : ""}`}
            >
              {primaryCta ? <CtaButton cta={primaryCta} variant="primary" /> : null}
              {secondaryCta ? <CtaButton cta={secondaryCta} variant="secondary" /> : null}
            </div>
          )}

          {badges && badges.length > 0 ? (
            <ul
              className={`mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[var(--primary-foreground)]/70 ${
                align === "center" ? "justify-center" : ""
              }`}
            >
              {badges.map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" />
                  {b}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {children ? (
          <div className="relative lg:col-span-5">{children}</div>
        ) : null}
      </div>
    </section>
  );
}
